<script setup>
import { ref, nextTick, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import EmojiPicker from 'dashboard/components-next/emoji/EmojiPicker.vue';
import SelectKnowledgeBaseDialog from 'dashboard/components-next/pilot/pageComponents/knowledgeBase/SelectKnowledgeBaseDialog.vue';
import FaqAnswerImages from 'dashboard/components-next/pilot/pageComponents/faq/FaqAnswerImages.vue';
import PilotFaqs from 'dashboard/api/pilot/faqs';
import MessageFormatter from 'shared/helpers/MessageFormatter.js';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const store = useStore();

const knowledgeBaseId = computed(() => Number(route.params.id));
const knowledgeBase = ref(null);
const isSubmitting = ref(false);

// 知识库切换弹窗
const selectKbDialogRef = ref(null);

// 图片路径
import faqThumbnail from 'assets/images/dashboard/pilot/faqs-light.svg';
import faqThumbnailDark from 'assets/images/dashboard/pilot/faqs-dark.svg';

// 图片加载错误处理
const handleThumbnailError = event => {
  event.target.style.display = 'none';
};

onMounted(() => {
  fetchKnowledgeBase();
});

// 获取知识库详情
const fetchKnowledgeBase = async () => {
  try {
    const response = await store.dispatch('pilotKnowledgeBases/show', {
      id: knowledgeBaseId.value,
    });
    knowledgeBase.value = response.payload;
  } catch (error) {
    console.error('Failed to fetch knowledge base:', error);
  }
};

// 监听路由参数变化，重新获取知识库数据
watch(
  () => route.params.id,
  (newId, oldId) => {
    if (newId && newId !== oldId) {
      fetchKnowledgeBase();
    }
  }
);

// 打开知识库选择弹窗
const openSelectKbDialog = () => {
  selectKbDialogRef.value?.open();
};

// 切换知识库
const handleKnowledgeBaseChange = newKbId => {
  router.replace({
    name: 'pilot_faqs_add',
    params: { id: newKbId },
  });
};

const question = ref('');
const answers = ref([
  { id: Date.now(), content: '', images: [], error: false },
]);
const hasUploadingImages = computed(() =>
  answers.value.some(answer => answer.isUploading)
);
const similarQuestions = ref([]);
const inputRefs = ref({});
const editorRefs = ref({});
import logoUrl from 'assets/images/dashboard/pilot/logo.png';

// 相似问题假数据
const exampleSimilarQuestions = [
  { question: '这个问题怎么理解？' },
  { question: '能详细解释一下吗？' },
  { question: '可以举个例子说明吗？' },
];

// 表情选择器
const openEmojiPickerId = ref(null);

// 切换表情选择器
const toggleEmojiPicker = answerId => {
  if (openEmojiPickerId.value === answerId) {
    openEmojiPickerId.value = null;
  } else {
    openEmojiPickerId.value = answerId;
  }
};

// 选择表情
const handleSelectEmoji = (emoji, answerId) => {
  const answer = answers.value.find(a => a.id === answerId);
  if (answer) {
    // 将表情追加到当前答案内容
    answer.content += emoji;
  }
  openEmojiPickerId.value = null;
};

// Markdown 渲染 - 使用 Chatwoot 的 MessageFormatter
const renderMarkdown = content => {
  if (!content) return '';
  const formatter = new MessageFormatter(content);
  return formatter.formattedMessage;
};

const addAnswer = () => {
  // 最多3个答案
  if (answers.value.length >= 3) {
    return;
  }
  const newId = Date.now();
  answers.value.push({
    id: newId,
    content: '',
    images: [],
    error: false,
  });
  nextTick(() => {
    // 尝试通过 DOM 聚焦编辑器
    const editorElement = document.querySelector(
      `[data-answer-id="${newId}"] .ProseMirror`
    );
    if (editorElement) {
      editorElement.focus();
    }
  });
};

// 处理回答编辑器失去焦点
const handleAnswerBlur = answerId => {
  const answer = answers.value.find(a => a.id === answerId);
  if (answer) {
    answer.error = !answer.content || !answer.content.trim();
  }
};

// 清除回答错误状态
const clearAnswerError = answerId => {
  const answer = answers.value.find(a => a.id === answerId);
  if (answer) {
    answer.error = false;
  }
};

const setInputRef = (id, el) => {
  if (el) {
    inputRefs.value[id] = el;
  }
};

const addSimilarQuestion = () => {
  // 最多10个相似问题
  if (similarQuestions.value.length >= 10) {
    return;
  }
  const newId = Date.now();
  const newItem = {
    id: newId,
    question: '',
    isEditing: true,
    error: false,
  };
  similarQuestions.value.unshift(newItem);
  nextTick(() => {
    const input = inputRefs.value[newId];
    if (input) {
      input.focus();
    }
  });
};

const removeSimilarQuestion = id => {
  const index = similarQuestions.value.findIndex(q => q.id === id);
  if (index !== -1) {
    similarQuestions.value.splice(index, 1);
  }
};

const editSimilarQuestion = id => {
  const questionItem = similarQuestions.value.find(q => q.id === id);
  if (questionItem) {
    questionItem.isEditing = true;
    questionItem.error = false;
    nextTick(() => {
      const input = inputRefs.value[id];
      if (input) {
        input.focus();
      }
    });
  }
};

const saveSimilarQuestion = id => {
  const questionItem = similarQuestions.value.find(q => q.id === id);
  if (!questionItem) return;

  if (!questionItem.question.trim()) {
    questionItem.error = true;
    questionItem.isEditing = true;
    return;
  }
  questionItem.error = false;
  questionItem.isEditing = false;
};

const removeAnswer = id => {
  if (answers.value.length <= 1) {
    useAlert('至少需要保留一个答案');
    return;
  }
  answers.value = answers.value.filter(a => a.id !== id);
};

const handleCancel = () => {
  router.push({
    name: 'pilot_knowledge_bases_faqs',
    params: { id: knowledgeBaseId.value },
  });
};

const handleSubmit = async (publish = false) => {
  if (hasUploadingImages.value) {
    useAlert(t('PILOT.KNOWLEDGE_BASES.FAQS.IMAGES.UPLOADING'));
    return;
  }
  if (!question.value.trim()) {
    useAlert(t('PILOT.KNOWLEDGE_BASES.FAQS.QUESTION_REQUIRED'));
    return;
  }

  // 验证所有答案是否已填写
  let hasEmptyAnswer = false;
  answers.value.forEach(answer => {
    if (!answer.content || !answer.content.trim()) {
      answer.error = true;
      hasEmptyAnswer = true;
    }
  });

  if (hasEmptyAnswer) {
    useAlert('请完善答案');
    return;
  }

  const validAnswers = answers.value.filter(a => a.content && a.content.trim());
  if (validAnswers.length === 0) {
    useAlert('至少需要填写一个答案');
    return;
  }

  // 验证相似问题：如果添加了相似问题，但不能有空的问题
  if (similarQuestions.value.length > 0) {
    const hasEmptyQuestion = similarQuestions.value.some(
      sq => !sq.question.trim()
    );
    if (hasEmptyQuestion) {
      useAlert('请完善相似问题');
      return;
    }
  }

  isSubmitting.value = true;

  try {
    const faqData = {
      knowledge_base_id: knowledgeBaseId.value,
      question: question.value,
      answers: validAnswers.map(a => ({
        answer: a.content.trim(),
        image_blob_ids: (a.images || []).map(image => image.blob_id),
      })),
      similar_questions: similarQuestions.value
        .filter(sq => sq.question.trim())
        .map(sq => sq.question.trim()),
      publish: publish,
    };

    await PilotFaqs.create(faqData);

    useAlert(
      publish
        ? t('PILOT.KNOWLEDGE_BASES.FAQS.CREATE_AND_PUBLISH_SUCCESS')
        : t('PILOT.KNOWLEDGE_BASES.FAQS.CREATE_SUCCESS')
    );

    router.push({
      name: 'pilot_knowledge_bases_faqs',
      params: { id: knowledgeBaseId.value },
    });
  } catch (error) {
    console.error('Failed to create FAQ:', error);
    const errorMessage =
      error?.response?.data?.message ||
      t('PILOT.KNOWLEDGE_BASES.FAQS.CREATE_ERROR');
    useAlert(errorMessage);
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <section
    class="flex flex-col w-full h-full bg-n-slate-white dark:bg-n-slate-800"
  >
    <!-- Loading Overlay -->
    <div
      v-if="isSubmitting"
      class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-3 backdrop-blur-sm"
    >
      <div class="flex flex-col items-center gap-3">
        <Spinner />
        <p class="text-sm text-n-slate-12 dark:text-n-slate-12">
          {{ t('PILOT.KNOWLEDGE_BASES.FAQS.CREATING') }}
        </p>
      </div>
    </div>

    <div class="flex flex-col h-full w-full">
      <!-- Header -->
      <header
        class="sticky top-0 z-10 px-6 bg-n-slate-white dark:bg-n-slate-800"
      >
        <div class="w-full mx-auto max-w-[60rem]">
          <div class="flex items-center h-20">
            <button
              type="button"
              class="p-2 -ml-2 text-n-slate-600 dark:text-n-slate-400 hover:bg-n-slate-100 dark:hover:bg-n-slate-700 rounded-lg transition-colors"
              @click="handleCancel"
            >
              <Icon icon="i-lucide-arrow-left" class="h-5 w-5" />
            </button>
            <h1
              class="ml-4 text-lg font-semibold text-n-slate-900 dark:text-n-slate-100"
            >
              {{ t('PILOT.KNOWLEDGE_BASES.FAQS.CREATE_TITLE') }}
            </h1>
          </div>
        </div>
      </header>

      <!-- Main Content -->
      <main
        class="flex-1 w-full px-6 overflow-auto bg-n-slate-white dark:bg-n-slate-800"
      >
        <div class="w-full mx-auto max-w-[60rem] py-6">
          <div class="flex flex-col gap-6">
            <!-- 虚线介绍区域 -->
            <section class="custom-dashed-border rounded-2xl py-5 px-6">
              <div
                class="flex flex-col md:flex-row items-start md:items-center gap-6"
              >
                <div
                  class="flex-shrink-0 w-[7.5rem] h-[6.5rem] rounded-lg flex items-center justify-center overflow-hidden"
                >
                  <img
                    :src="faqThumbnail"
                    :alt="t('PILOT.KNOWLEDGE_BASES.FAQS.ADD.GUIDE_TITLE')"
                    class="w-full h-full object-cover rounded-lg block dark:hidden"
                    @error="handleThumbnailError"
                  />
                  <img
                    :src="faqThumbnailDark"
                    :alt="t('PILOT.KNOWLEDGE_BASES.FAQS.ADD.GUIDE_TITLE')"
                    class="w-full h-full object-cover rounded-lg hidden dark:block"
                    @error="handleThumbnailError"
                  />
                </div>
                <div class="flex flex-col flex-1 gap-3">
                  <p class="text-n-slate-12 text-sm font-medium">
                    {{ t('PILOT.KNOWLEDGE_BASES.FAQS.ADD.GUIDE_TITLE') }}
                  </p>
                  <p class="text-n-slate-11 text-sm">
                    {{ t('PILOT.KNOWLEDGE_BASES.FAQS.ADD.GUIDE_DESCRIPTION') }}
                  </p>
                </div>
              </div>
            </section>

            <!-- 知识库信息（锁定状态） -->
            <div
              class="w-full flex items-start justify-between gap-3 p-4 rounded-xl outline-1 outline outline-n-container bg-n-solid-2 text-left"
            >
              <!-- Logo -->
              <div
                class="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden"
                :class="knowledgeBase?.logo_url ? '' : 'bg-n-brand/10'"
              >
                <img
                  v-if="knowledgeBase?.logo_url"
                  :src="knowledgeBase.logo_url"
                  :alt="knowledgeBase?.name"
                  class="w-full h-full object-cover"
                />
                <span v-else class="text-base font-semibold text-n-brand">
                  {{ knowledgeBase?.name?.charAt(0)?.toUpperCase() || '?' }}
                </span>
              </div>

              <!-- 信息 -->
              <div class="flex flex-col min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <span class="text-sm text-n-slate-12 font-medium">{{
                    knowledgeBase?.name || '加载中...'
                  }}</span>
                  <span
                    v-if="knowledgeBase?.knowledge_base_type === 'faq'"
                    class="px-2 py-0.5 text-xs rounded-full font-medium bg-n-emerald-100 text-n-emerald-700 dark:bg-n-emerald-900/30 dark:text-n-emerald-400"
                  >
                    FAQs
                  </span>
                </div>
                <span class="text-sm text-n-slate-11 mt-2">
                  {{
                    knowledgeBase?.description ||
                    t('PILOT.KNOWLEDGE_BASES.SELECT_KB_DIALOG.NO_DESCRIPTION')
                  }}
                </span>
                <!-- 文档和FAQ数量 -->
                <div
                  class="flex items-center gap-4 text-sm text-n-slate-11 mt-2"
                >
                  <div class="flex items-center gap-1.5">
                    <span class="i-lucide-file-text size-3" />
                    <span>{{ knowledgeBase?.documents_count || 0 }} 文档</span>
                  </div>
                  <div class="flex items-center gap-1.5">
                    <span class="i-lucide-message-circle-question size-3" />
                    <span>{{ knowledgeBase?.faqs_count || 0 }} FAQ</span>
                  </div>
                </div>
              </div>

              <!-- 切换按钮 -->
              <button
                type="button"
                class="flex items-center gap-1 text-sm text-n-slate-9 hover:text-n-brand flex-shrink-0 transition-colors"
                @click="openSelectKbDialog"
              >
                <span class="i-lucide-arrow-right-left size-4" />
                <span>{{ t('PILOT.KNOWLEDGE_BASES.SWITCH_KB') }}</span>
              </button>
            </div>

            <!-- 问题区域 -->
            <div class="flex flex-col gap-5">
              <h2
                class="text-n-slate-12 dark:text-n-slate-12 text-base font-medium"
              >
                问题
              </h2>

              <!-- 标准问题 -->
              <div>
                <label
                  class="block text-n-slate-12 dark:text-n-slate-12 text-sm font-medium mb-2"
                >
                  {{ t('PILOT.KNOWLEDGE_BASES.FAQS.QUESTION_LABEL') }}
                  <span class="text-n-brand-500">*</span>
                </label>
                <input
                  v-model="question"
                  type="text"
                  :placeholder="
                    t('PILOT.KNOWLEDGE_BASES.FAQS.QUESTION_PLACEHOLDER')
                  "
                  maxlength="200"
                  class="w-full px-4 py-3 bg-n-slate-white dark:bg-n-slate-800 border border-n-weak rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-n-brand-500 no-margin"
                />
                <p class="text-xs text-n-slate-11 mt-1 text-right">
                  {{ question?.length || 0 }}/200
                </p>
              </div>

              <!-- 相似问题 -->
              <div>
                <div class="mb-3">
                  <label
                    class="text-n-slate-12 dark:text-n-slate-12 text-sm font-medium mb-2 block"
                  >
                    {{ t('PILOT.KNOWLEDGE_BASES.FAQS.SIMILAR_QUESTIONS') }}
                  </label>
                  <p class="text-n-slate-11 dark:text-n-slate-11 text-sm">
                    作为标准问题的补充，衍生相同意思的问题，提高用户问题的命中率。
                  </p>
                </div>

                <!-- 假数据引导区（没有相似问题时显示） -->
                <div
                  v-if="similarQuestions.length === 0"
                  class="relative w-full"
                >
                  <!-- 假数据列表（带透明度） -->
                  <div class="w-full space-y-3 opacity-50 pointer-events-none">
                    <div
                      v-for="(sq, index) in exampleSimilarQuestions.slice(0, 3)"
                      :key="`example-sq-${index}`"
                      class="flex items-start gap-4 p-4 bg-n-slate-white dark:bg-n-slate-800 rounded-lg border border-n-weak"
                    >
                      <!-- 图标 -->
                      <div
                        class="h-10 w-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-n-brand text-white"
                      >
                        <Icon icon="i-lucide-help-circle" class="h-5 w-5" />
                      </div>
                      <!-- 问题文本 -->
                      <span
                        class="text-sm text-n-slate-700 dark:text-n-slate-300 flex-1"
                      >
                        {{ sq.question }}
                      </span>
                    </div>
                  </div>
                  <!-- 渐变蒙层 + 内容 -->
                  <div
                    class="absolute inset-x-0 bottom-0 flex flex-col items-center justify-end w-full bg-gradient-to-t from-n-solid-2 from-25% to-transparent pt-24 pb-6"
                  >
                    <div
                      class="flex flex-col items-center justify-center gap-6"
                    >
                      <div
                        class="flex flex-col items-center justify-center gap-3"
                      >
                        <h2
                          class="text-3xl font-medium text-center text-n-slate-12"
                        >
                          添加相似问题
                        </h2>
                        <p
                          class="max-w-xl text-base text-center text-n-slate-11"
                        >
                          作为标准问题的补充，提高用户问题的命中率
                        </p>
                      </div>
                      <Button
                        :label="
                          t('PILOT.KNOWLEDGE_BASES.FAQS.ADD_SIMILAR_QUESTION')
                        "
                        icon="i-lucide-plus"
                        blue
                        @click="addSimilarQuestion"
                      />
                    </div>
                  </div>
                </div>

                <!-- 有相似问题时显示真实列表 -->
                <div v-else class="space-y-2">
                  <!-- 已添加的相似问题 -->
                  <div
                    v-for="sq in similarQuestions"
                    :key="sq.id"
                    class="w-full flex items-center gap-3 p-4 bg-n-slate-white dark:bg-n-slate-800 border border-n-weak rounded-lg"
                  >
                    <!-- 显示模式 -->
                    <template v-if="!sq.isEditing">
                      <div
                        class="h-10 w-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-n-brand text-white"
                      >
                        <Icon icon="i-lucide-help-circle" class="h-5 w-5" />
                      </div>
                      <span
                        class="flex-1 text-sm text-n-slate-700 dark:text-n-slate-300 cursor-pointer"
                        @click="editSimilarQuestion(sq.id)"
                      >
                        {{ sq.question }}
                      </span>
                      <div class="flex items-center gap-2 shrink-0">
                        <Button
                          icon="i-lucide-pencil"
                          size="xs"
                          color="slate"
                          variant="faded"
                          @click="editSimilarQuestion(sq.id)"
                        />
                        <Button
                          icon="i-lucide-trash-2"
                          size="xs"
                          color="ruby"
                          variant="faded"
                          @click="removeSimilarQuestion(sq.id)"
                        />
                      </div>
                    </template>

                    <!-- 编辑模式 -->
                    <template v-else>
                      <div class="w-full">
                        <div class="flex items-center gap-3">
                          <div class="flex-1">
                            <Input
                              :ref="el => setInputRef(sq.id, el)"
                              v-model="sq.question"
                              :placeholder="
                                t(
                                  'PILOT.KNOWLEDGE_BASES.FAQS.SIMILAR_QUESTION_PLACEHOLDER'
                                )
                              "
                              :message-type="sq.error ? 'error' : 'info'"
                              size="sm"
                              maxlength="200"
                              @blur="saveSimilarQuestion(sq.id)"
                              @keyup.enter="saveSimilarQuestion(sq.id)"
                            />
                          </div>
                          <div class="flex items-center gap-2 shrink-0">
                            <Button
                              icon="i-lucide-check"
                              size="xs"
                              color="slate"
                              variant="faded"
                              @click="saveSimilarQuestion(sq.id)"
                            />
                            <Button
                              icon="i-lucide-trash-2"
                              size="xs"
                              color="ruby"
                              variant="faded"
                              @click="removeSimilarQuestion(sq.id)"
                            />
                          </div>
                        </div>
                        <p class="text-xs text-n-slate-11 mt-1 text-right">
                          {{ sq.question?.length || 0 }}/200
                        </p>
                      </div>
                    </template>
                  </div>
                  <!-- 添加更多相似问题按钮 -->
                  <button
                    v-if="
                      similarQuestions.length > 0 &&
                      similarQuestions.length < 10
                    "
                    type="button"
                    class="w-full text-n-brand hover:bg-n-alpha-2 text-sm font-medium transition-colors duration-200 rounded-lg px-4 py-3 inline-flex items-center justify-center gap-1.5 border border-dashed border-n-brand/30"
                    @click="addSimilarQuestion"
                  >
                    <Icon icon="i-lucide-plus" class="h-4 w-4 shrink-0" />
                    {{ t('PILOT.KNOWLEDGE_BASES.FAQS.ADD_SIMILAR_QUESTION') }}
                  </button>
                </div>
              </div>
            </div>

            <!-- 答案区域 -->
            <div class="flex flex-col gap-5">
              <div>
                <h2
                  class="text-n-slate-12 dark:text-n-slate-12 text-base font-medium mb-2"
                >
                  答案 <span class="text-n-brand-500">*</span>
                </h2>
                <p class="text-n-slate-11 dark:text-n-slate-11 text-sm">
                  系统将会把配置的答案，都推送给用户
                </p>
              </div>

              <div class="flex flex-col gap-5">
                <div
                  v-for="answer in answers"
                  :key="answer.id"
                  class="border border-n-weak rounded-lg"
                >
                  <!-- 工具栏 -->
                  <div class="flex items-center justify-between px-3 py-2">
                    <div class="flex items-center gap-2 relative">
                      <button
                        type="button"
                        class="p-1.5 text-n-slate-7 hover:text-n-slate-9 rounded transition-colors"
                        @click="toggleEmojiPicker(answer.id)"
                      >
                        <Icon icon="i-lucide-smile" class="h-4 w-4" />
                      </button>
                      <EmojiPicker
                        :is-open="openEmojiPickerId === answer.id"
                        @select-emoji="
                          emoji => handleSelectEmoji(emoji, answer.id)
                        "
                        @close="openEmojiPickerId = null"
                      />
                    </div>
                    <button
                      class="p-1.5 text-n-slate-7 hover:text-n-ruby-9 rounded transition-colors"
                      @click="removeAnswer(answer.id)"
                    >
                      <Icon icon="i-lucide-trash-2" class="h-4 w-4" />
                    </button>
                  </div>

                  <!-- 编辑器 + 预览 (70% + 30%) -->
                  <div
                    class="flex gap-3 px-3 pb-3 items-stretch"
                    :data-answer-id="answer.id"
                  >
                    <!-- 左侧编辑器 70% -->
                    <div class="w-[70%] min-h-[160px]">
                      <Editor
                        :ref="
                          el => {
                            if (el) editorRefs[answer.id] = el;
                          }
                        "
                        v-model="answer.content"
                        placeholder="编辑机器人答案"
                        :max-length="1000"
                        :show-character-count="true"
                        :focus-on-mount="false"
                        :message-type="answer.error ? 'error' : 'info'"
                        class="border-0 rounded-none h-full"
                        @blur="handleAnswerBlur(answer.id)"
                        @focus="clearAnswerError(answer.id)"
                      />
                    </div>

                    <!-- 右侧预览 30% -->
                    <div class="w-[30%] flex-shrink-0">
                      <div class="flex items-end gap-2 h-full">
                        <!-- 机器人头像 -->
                        <div class="flex-shrink-0">
                          <img
                            :src="logoUrl"
                            alt="机器人"
                            class="w-11 h-11 rounded-full object-contain"
                          />
                        </div>

                        <!-- 预览气泡 -->
                        <div class="flex-1">
                          <div
                            class="faq-preview-bubble bg-n-solid-iris text-n-slate-12 p-3 rounded-2xl rounded-bl-none shadow-lg"
                          >
                            <div
                              v-if="answer.content"
                              v-dompurify-html="renderMarkdown(answer.content)"
                              class="prose prose-bubble"
                            />
                            <div
                              v-else
                              class="text-sm text-n-slate-11 dark:text-n-slate-11 text-center"
                            >
                              答案预览
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <FaqAnswerImages
                    v-model="answer.images"
                    :disabled="isSubmitting"
                    @uploading-change="answer.isUploading = $event"
                  />
                </div>
                <!-- 添加答案按钮 -->
                <button
                  type="button"
                  :disabled="answers.length >= 3"
                  class="text-sm font-medium transition-colors duration-200 rounded-lg px-3 py-2 inline-flex items-center gap-1.5"
                  :class="
                    answers.length >= 3
                      ? 'text-n-slate-8 cursor-not-allowed'
                      : 'text-n-brand hover:bg-n-alpha-2'
                  "
                  @click="addAnswer"
                >
                  <Icon icon="i-lucide-plus" class="h-4 w-4 shrink-0" />
                  <span>添加答案</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <!-- 底部固定按钮 -->
      <footer
        class="sticky bottom-0 z-10 px-6 py-4 bg-n-slate-white dark:bg-n-slate-800"
      >
        <div class="w-full mx-auto max-w-[60rem] flex justify-end gap-3">
          <Button
            :label="t('PILOT.KNOWLEDGE_BASES.FAQS.CANCEL')"
            variant="faded"
            color="slate"
            @click="handleCancel"
          />
          <Button
            :label="t('PILOT.KNOWLEDGE_BASES.FAQS.SAVE_DRAFT')"
            icon="i-lucide-save"
            variant="faded"
            color="slate"
            :disabled="isSubmitting || hasUploadingImages"
            @click="handleSubmit(false)"
          />
          <Button
            :label="t('PILOT.KNOWLEDGE_BASES.FAQS.CREATE_AND_PUBLISH')"
            icon="i-lucide-upload-cloud"
            blue
            :disabled="isSubmitting || hasUploadingImages"
            @click="handleSubmit(true)"
          />
        </div>
      </footer>
    </div>
  </section>

  <!-- 知识库选择弹窗 -->
  <SelectKnowledgeBaseDialog
    ref="selectKbDialogRef"
    action-type="switch"
    @select="handleKnowledgeBaseChange"
  />
</template>

<style scoped>
/* 重置 input 默认 margin */
input[type='text'] {
  margin-bottom: 0;
}

:deep(.ProseMirror-woot-style) {
  min-height: 140px;
  max-height: none !important;
  height: auto;
}

:deep(.ProseMirror-menubar-wrapper) {
  height: 100%;
  display: flex;
  flex-direction: column;
}

:deep(.ProseMirror-menubar-wrapper .ProseMirror) {
  flex: 1;
  min-height: 140px;
  max-height: none !important;
}
</style>
